import React from 'react';
import classNames from 'classnames';

import Field, { hasDataInFields } from '../../Field';

import SectionContainer from '../SectionContainer';
import css from './SectionHero.module.css';
import {
  HeroInfoReveal,
  HeroTitleReveal,
} from '../../../../components/GsapAnimation/HeroTitleReveal/HeroTitleReveal';
import { shouldDisableCmsPageAnimations } from '../../../../util/cmsPageAnimations';

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * Section component for a website's hero section
 * The Section Hero doesn't have any Blocks by default, all the configurations are made in the Section Hero settings
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.defaultClasses
 * @param {string} props.defaultClasses.sectionDetails
 * @param {string} props.defaultClasses.title
 * @param {string} props.defaultClasses.description
 * @param {string} props.defaultClasses.ctaButton
 * @param {string} props.sectionId id of the section
 * @param {'hero'} props.sectionType
 * @param {Object?} props.title
 * @param {Object?} props.description
 * @param {Object?} props.appearance
 * @param {Object?} props.callToAction
 * @param {Object} props.options extra options for the section component (e.g. custom fieldComponents)
 * @param {Object<string,FieldComponentConfig>?} props.options.fieldComponents custom fields
 * @returns {JSX.Element} Section for article content
 */
const SectionHero = props => {
  const {
    sectionId,
    className,
    rootClassName,
    defaultClasses,
    title,
    description,
    appearance,
    callToAction,
    options,
  } = props;

  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const fieldOptions = options || {};
  const disableAnimations = shouldDisableCmsPageAnimations(fieldOptions.pageId);

  const hasHeaderFields = hasDataInFields([title, description, callToAction], fieldOptions);

  return (
    <SectionContainer
      id={sectionId}
      className={className}
      rootClassName={classNames(rootClassName || css.root)}
      appearance={appearance}
      options={fieldOptions}
    >
      {hasHeaderFields ? (
        <header className={defaultClasses.sectionDetails}>
          {disableAnimations ? (
            <Field data={title} className={defaultClasses.title} options={fieldOptions} />
          ) : (
            <HeroTitleReveal
              staggerAmount={0.3}
              duration={0.8}
              fromYPercent={30}
              triggerStart="top 100%"
              delay={0}
              markers={false}
              className={css.animatedTitle}
            >
              <Field data={title} className={defaultClasses.title} options={fieldOptions} />
            </HeroTitleReveal>
          )}

          {disableAnimations ? (
            <Field
              data={description}
              className={defaultClasses.description}
              options={fieldOptions}
            />
          ) : (
            <HeroInfoReveal
              delay={0.1}
              duration={0.5}
              fromYPercent={30}
              staggerAmount={0.3}
              markers={false}
              triggerStart="0 100%"
            >
              <Field
                data={description}
                className={defaultClasses.description}
                options={fieldOptions}
              />
            </HeroInfoReveal>
          )}

          {disableAnimations ? (
            <Field data={callToAction} className={defaultClasses.ctaButton} options={fieldOptions} />
          ) : (
            <HeroInfoReveal
              delay={0.15}
              duration={0.5}
              fromYPercent={30}
              staggerAmount={0.3}
              markers={false}
              triggerStart="0 100%"
            >
              <Field
                data={callToAction}
                className={defaultClasses.ctaButton}
                options={fieldOptions}
              />
            </HeroInfoReveal>
          )}
        </header>
      ) : null}
    </SectionContainer>
  );
};

export default SectionHero;
